import { motion } from "framer-motion";
import { Heart, Phone, ArrowRight, Leaf } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSection = ({ adminSettings }) => {
  const profileImage = adminSettings?.profile_image_url 
    ? `${BACKEND_URL}${adminSettings.profile_image_url}`
    : null;

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#F9F8F4] to-[#E8E4D9]"
      data-testid="hero-section"
    >
      {/* Background plant decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute -left-20 bottom-0 w-[400px] h-[500px] opacity-10"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1764072970428-25922d1e3b85?w=600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to right, black 60%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, black 60%, transparent)"
          }}
        />
        <div 
          className="absolute -right-10 top-1/4 w-[350px] h-[450px] opacity-8"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1760184347560-f7463e36859a?w=600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to left, black 60%, transparent)",
            WebkitMaskImage: "linear-gradient(to left, black 60%, transparent)"
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-left"
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-sm uppercase tracking-widest text-[#8F9E82] font-bold mb-4"
            >
              End of Life Doula Services
            </motion.p>
            
            <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl lg:text-7xl font-medium text-[#2D2A26] leading-tight mb-6">
              {adminSettings?.owner_name || "Benevolent Benefit"}
            </h1>
            
            <p className="text-xl md:text-2xl text-[#5C5852] font-light mb-4 font-['Playfair_Display'] italic">
              {adminSettings?.owner_title || "End of Life Doula & Companion"}
            </p>
            
            <p className="text-base md:text-lg text-[#5C5852] leading-relaxed mb-8 max-w-xl">
              Providing compassionate care, unwavering support, and gentle guidance 
              for those transitioning and their families. Because no one should 
              face life's final journey alone.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ y: -2, boxShadow: "0 8px 30px rgba(74, 93, 35, 0.25)" }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="bg-[#4A5D23] text-white px-8 py-4 rounded-full font-medium flex items-center justify-center gap-2 group"
                data-testid="hero-cta-consultation"
              >
                <Heart className="w-5 h-5" />
                Schedule Consultation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              
              <motion.a
                href="tel:504-374-8174"
                whileHover={{ y: -2 }}
                className="bg-[#E8E4D9] text-[#2D2A26] px-8 py-4 rounded-full font-medium flex items-center justify-center gap-2"
                data-testid="hero-cta-call"
              >
                <Phone className="w-5 h-5" />
                504-374-8174
              </motion.a>
            </div>

            {/* Credentials */}
            {adminSettings?.credentials && adminSettings.credentials.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="mt-8 pt-8 border-t border-[#E8E4D9]"
              >
                <p className="text-xs uppercase tracking-widest text-[#8F9E82] font-bold mb-3">Credentials</p>
                <div className="flex flex-wrap gap-2">
                  {adminSettings.credentials.map((cred, idx) => (
                    <span 
                      key={idx}
                      className="bg-white/60 px-3 py-1 rounded-full text-sm text-[#4A5D23] border border-[#4A5D23]/10"
                    >
                      {cred}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Profile Image or Decorative Element */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative w-full max-w-md mx-auto">
              {/* Decorative shape behind image */}
              <div className="absolute inset-0 bg-[#8F9E82]/20 rounded-[60%_40%_50%_50%/50%_60%_40%_50%] transform rotate-6 scale-105" />
              
              {profileImage ? (
                <div className="relative overflow-hidden rounded-[60%_40%_50%_50%/50%_60%_40%_50%] aspect-[4/5]">
                  <img
                    src={profileImage}
                    alt={adminSettings?.owner_name || "End of Life Doula"}
                    className="w-full h-full object-cover"
                    data-testid="hero-profile-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#4A5D23]/20 to-transparent" />
                </div>
              ) : (
                <div className="relative overflow-hidden rounded-[60%_40%_50%_50%/50%_60%_40%_50%] aspect-[4/5] bg-gradient-to-br from-[#8F9E82]/30 to-[#4A5D23]/20">
                  <div 
                    className="absolute inset-0 opacity-20"
                    style={{
                      backgroundImage: "url('https://images.unsplash.com/photo-1764072970428-25922d1e3b85?w=600')",
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center p-8">
                      <Leaf className="w-16 h-16 text-[#4A5D23] mx-auto mb-4" />
                      <p className="font-['Playfair_Display'] text-2xl text-[#4A5D23] italic">
                        Compassion & Care
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg border border-stone-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#4A5D23]/10 rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6 text-[#4A5D23]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2D2A26]">Compassionate Care</p>
                    <p className="text-xs text-[#5C5852]">Every step of the way</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
